import React, { useEffect, useMemo, useState } from 'react';
import emailService, { EmailPlaybookMeta, EmailPlaybookStats } from '../../../services/emailService';
import { toast } from 'react-hot-toast';

interface PlaybookSelectorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (playbook: EmailPlaybookMeta) => void;
}

const PlaybookSelectorModal: React.FC<PlaybookSelectorModalProps> = ({ isOpen, onClose, onSelect }) => {
  const [playbooks, setPlaybooks] = useState<EmailPlaybookMeta[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [goalFilter, setGoalFilter] = useState<string>('all');
  const [industryFilter, setIndustryFilter] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [statsById, setStatsById] = useState<Record<string, EmailPlaybookStats | undefined>>({});
  const [isLoadingStats, setIsLoadingStats] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;

    const load = async () => {
      try {
        setIsLoading(true);
        const meta = await emailService.getPlaybooks();
        if (!cancelled) {
          setPlaybooks(meta);
        }
      } catch (error) {
        console.error('Error loading playbooks:', error);
        toast.error('Failed to load playbooks');
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !playbooks.length) return;
    let cancelled = false;

    const loadStats = async () => {
      try {
        setIsLoadingStats(true);
        const results = await Promise.all(
          playbooks.map(async (pb) => {
            try {
              const stats = await emailService.getPlaybookStats(pb.id);
              return { id: pb.id, stats };
            } catch (error) {
              console.error('Error loading playbook stats for', pb.id, error);
              return null;
            }
          })
        );

        if (cancelled) return;

        const next: Record<string, EmailPlaybookStats> = {};
        for (const entry of results) {
          if (entry && entry.stats) {
            next[entry.id] = entry.stats;
          }
        }
        setStatsById(next);
      } finally {
        if (!cancelled) {
          setIsLoadingStats(false);
        }
      }
    };

    loadStats();

    return () => {
      cancelled = true;
    };
  }, [isOpen, playbooks]);

  const recommendedIds = useMemo(() => {
    const map = new Map<string, { id: string; replyRate: number }>();

    playbooks.forEach((pb) => {
      const stats = statsById[pb.id];
      if (!stats || !stats.campaignCount || !stats.totals.sent) return;
      const key = `${pb.goal}|${(pb.industries[0] || 'any').toLowerCase()}`;
      const current = map.get(key);
      if (!current || stats.replyRate > current.replyRate) {
        map.set(key, { id: pb.id, replyRate: stats.replyRate });
      }
    });

    return new Set(Array.from(map.values()).map((v) => v.id));
  }, [playbooks, statsById]);

  const goals = Array.from(new Set(playbooks.map((p) => p.goal))).sort();
  const industries = Array.from(new Set(playbooks.flatMap((p) => p.industries))).sort();

  if (!isOpen) return null;

  const goalTips: Record<string, string> = {
    pipeline: 'Great general outbound starter. Aim for 4-6 emails, spaced 3-4 days apart.',
    demo: 'Optimized to book meetings. 3-5 emails is usually enough for beginners.',
    trial: 'Use this when offering a free trial. Focus on quick wins and activation.',
    nurture: 'Warm leads slowly with value-first content every 7-10 days.',
    reactivation: 'Shorter sequences (3-4 emails) work best for cold lists.',
    event: 'Start 2-3 weeks before the event, with 3-5 total touches.',
    referral: 'Best used after you have some happy customers or warm relationships.',
    partnership: 'Keep it concise and value-focused, 3-4 emails max.',
    recruiting: 'Treat candidates like VIPs; keep follow-ups polite and spaced out.',
  };

  const filtered = playbooks.filter((p) => {
    if (goalFilter !== 'all' && p.goal !== goalFilter) return false;
    if (industryFilter !== 'all' && !p.industries.includes(industryFilter)) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!p.name.toLowerCase().includes(q) && !p.description.toLowerCase().includes(q)) {
        return false;
      }
    }
    return true;
  });

  const selected = playbooks.find((p) => p.id === selectedId) || null;

  const handleConfirm = () => {
    if (!selected) {
      toast.error('Please select a playbook');
      return;
    }
    onSelect(selected);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <h2 className="text-lg font-semibold">Choose a Playbook</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-sm"
          >
            Close
          </button>
        </div>
        <div className="px-6 py-3 border-b space-y-3">
          <div className="bg-blue-50 border border-blue-100 rounded-md px-3 py-2 text-xs text-blue-900">
            <div className="font-semibold mb-1">New to cold email?</div>
            <p className="mb-1">
              Start with a recommended playbook for your goal. We'll pre-fill a proven sequence so you can edit
              instead of writing from scratch.
            </p>
            <p>
              For most beginners,
              <span className="font-semibold"> 4-6 emails</span>
              <span className="font-semibold"> every 3-4 days</span>
              is a safe starting point.
            </p>
          </div>

          <div className="flex flex-col md:flex-row gap-3">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or description..."
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
            <select
              value={goalFilter}
              onChange={(e) => setGoalFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm"
            >
              <option value="all">All goals</option>
              {goals.map((goal) => (
                <option key={goal} value={goal}>
                  {goal}
                </option>
              ))}
            </select>
            <select
              value={industryFilter}
              onChange={(e) => setIndustryFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm"
            >
              <option value="all">All industries</option>
              {industries.map((ind) => (
                <option key={ind} value={ind}>
                  {ind}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
          {isLoading ? (
            <div className="text-sm text-gray-500">Loading playbooks...</div>
          ) : filtered.length === 0 ? (
            <div className="text-sm text-gray-500">No playbooks match your filters yet.</div>
          ) : (
            filtered.map((pb) => (
              <button
                key={pb.id}
                type="button"
                onClick={() => setSelectedId(pb.id)}
                className={`w-full text-left border rounded-md p-3 text-sm mb-1 transition-colors ${
                  selectedId === pb.id
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-blue-200 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <div>
                    <div className="flex items-center gap-2">
                      <div className="font-semibold text-gray-900">{pb.name}</div>
                      {recommendedIds.has(pb.id) && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-semibold border border-emerald-200">
                          Recommended for beginners
                        </span>
                      )}
                    </div>
                    <div className="mt-0.5 text-[11px] text-gray-500">
                      {goalTips[pb.goal] || 'Solid default sequence for this goal.'}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1 text-xs text-gray-500">
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 rounded-full border border-gray-200">
                        {pb.goal}
                      </span>
                      <span className="px-2 py-0.5 rounded-full border border-gray-200">
                        {pb.stepsCount} steps
                      </span>
                    </div>
                    {statsById[pb.id] && (
                      <div className="text-[11px] text-gray-500">
                        <span className="mr-2">Reply {statsById[pb.id]!.replyRate.toFixed(1)}%</span>
                        <span className="mr-2">Open {statsById[pb.id]!.openRate.toFixed(1)}%</span>
                        <span>{statsById[pb.id]!.campaignCount} campaigns</span>
                      </div>
                    )}
                    {!statsById[pb.id] && isLoadingStats && (
                      <div className="text-[11px] text-gray-400">Loading performance stats...</div>
                    )}
                  </div>
                </div>
                <p className="text-xs text-gray-600 mb-1">{pb.description}</p>
                <p className="text-[11px] text-gray-500">
                  {pb.industries.join(', ')} · {pb.useCases.join(', ')}
                </p>
              </button>
            ))
          )}
        </div>
        <div className="px-6 py-3 border-t flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            className="px-4 py-2 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700"
            disabled={!selected}
          >
            Use Playbook
          </button>
        </div>
      </div>
    </div>
  );
};

export default PlaybookSelectorModal;
